import React, {ReactNode, useContext, useEffect} from 'react';

import UserContext from './UserContext';
import {useNavigate} from 'react-router-dom';
import Session, {
  SessionAuth,
  SessionContext,
  doesSessionExist,
  useSessionContext,
} from 'supertokens-auth-react/recipe/session';
import {signOut} from 'supertokens-auth-react/recipe/emailpassword';

import {ApiError, UserService, newUser_return} from '../../services/openapi';
import APIWrapperContext from '../../services/APIWrapperContext';
import PushNotificationService from '../../services/PushNotificationService';

interface IUserContextProviderProps {
  children?: ReactNode;
}

const UserContextProvider = ({children}: IUserContextProviderProps) => {
  // Lift the value into the parent's state: https://reactjs.org/docs/context.html#caveats
  const [name, setName] = React.useState<string | undefined>(undefined);
  const [email, setEmail] = React.useState<string | undefined>(undefined);
  const [user_roll, setUserRoll] = React.useState<string[] | undefined>(undefined);
  const [additionalUserdata, setAdditionalUserdata] = React.useState<any | undefined>(undefined);
  const [checkingUser, setCheckingUser] = React.useState(true);
  let sessionContext = useSessionContext();
  const {call_backend_api} = React.useContext(APIWrapperContext);
  // const { token,setAuthed,setCheckingAuthed } = React.useContext(SecurityContext)

  const USER_ROLES_KEY = 'userRoles';

  const requestNotifications = async (user_role: string[]) => {
    try {
      // Check if user has the required role
      const allowedRoles = ['Zeiterfassung Mitarbeiter', 'Administrator', 'Admin Aufgaben'];
      const hasAllowedRole = user_role.some((role) => allowedRoles.includes(role));
      console.log(hasAllowedRole, user_role);
      if (!hasAllowedRole) {
        console.log(
          'Benachrichtigungen werden für diesen Benutzer nicht angefordert - keine berechtigte Rolle'
        );
        return;
      }

      const pushService = PushNotificationService.getInstance();
      if (pushService.isSupported()) {
        const hasPermission = await pushService.requestNotificationPermission();
        if (hasPermission) {
          await pushService.subscribeToPushNotifications();
        }
      }
    } catch (error) {
      console.error('Fehler beim Aktivieren der Push-Benachrichtigungen:', error);
    }
  };

  const reset_NewDocumentNotifications = () => {
    call_backend_api(
      UserService.resetNotificationsUserResetNotificationsPost(),
      () => {
        update_Additional_Userdata();
      },
      undefined,
      undefined,
      false
    );
  };

  const update_Additional_Userdata = () => {
    call_backend_api(
      UserService.meAdditionalUserDataUserMeAdditionalUserDataGet(),
      (t: any) => setAdditionalUserdata(t),
      undefined,
      undefined,
      false
    );
  };

  const fetchUserData = React.useCallback(() => {
    call_backend_api(
      UserService.readUsersMeUserMeGet(),
      (res: newUser_return) => {
        const newRoles = res.userclass;

        // Check if roles have changed
        const storedRolesStr = localStorage.getItem(USER_ROLES_KEY);
        if (storedRolesStr) {
          try {
            const storedRoles = JSON.parse(storedRolesStr) as string[];

            // Compare roles
            const rolesChanged =
              storedRoles.length !== newRoles.length ||
              !storedRoles.every((role) => newRoles.includes(role)) ||
              !newRoles.every((role) => storedRoles.includes(role));

            if (rolesChanged) {
              // Roles have changed - logout the user
              (async () => {
                localStorage.removeItem(USER_ROLES_KEY);
                await signOut();
                window.location.href = '/';
              })();
              return; // Exit early, don't update state
            }
          } catch (error) {
            // If there's an error parsing stored roles, treat as first login
          }
        }

        // Store current roles in localStorage
        localStorage.setItem(USER_ROLES_KEY, JSON.stringify(newRoles));

        // Update state
        setEmail(res.email);
        setName(res.fullname);
        setUserRoll(res.userclass);
        // Request notifications after user data is loaded
        requestNotifications(res.userclass);
      },
      (err: ApiError) => {},
      undefined,
      false,
      undefined,
      undefined,
      true
    );

    update_Additional_Userdata();
  }, [call_backend_api]);

  React.useEffect(() => {
    fetchUserData();
  }, [(sessionContext as any).doesSessionExist]);

  // Auto-refresh user data when window regains focus
  React.useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchUserData();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [fetchUserData]);

  // Clean up roles from localStorage when session ends
  React.useEffect(() => {
    if (!sessionContext.loading && !(sessionContext as any).doesSessionExist) {
      // Session doesn't exist - clear stored roles
      localStorage.removeItem(USER_ROLES_KEY);
    }
  }, [sessionContext.loading, (sessionContext as any).doesSessionExist]);

  return (
    <UserContext.Provider
      value={{
        name,
        email,
        user_roll,
        setUserRoll,
        setName,
        setEmail,
        checkingUser,
        additionalUserdata,
        reset_NewDocumentNotifications,
        refreshUserData: fetchUserData,
      }}>
      {children}
    </UserContext.Provider>
  );
};

export default UserContextProvider;
